import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, MessageCircle, Loader2, Sparkles, Hand } from "lucide-react";
import { toast } from "sonner";
import { renderShareCardCanvas } from "@/lib/canvasCard";
import { api } from "@/lib/api";
import { rp } from "@/lib/format";

/**
 * Bottom sheet — CLIENT-FIRST render (v4 design selalu konsisten).
 * Blob kartu di-cache per product.id → buka barang yang sama = instant.
 * User long-presses the preview image to save it natively.
 */

// Module-level blob cache: product.id → objectURL (max 20, FIFO)
const _cardBlobCache = new Map();
function cacheCard(id, url) {
  if (_cardBlobCache.size >= 20) {
    const oldest = _cardBlobCache.keys().next().value;
    URL.revokeObjectURL(_cardBlobCache.get(oldest));
    _cardBlobCache.delete(oldest);
  }
  _cardBlobCache.set(id, url);
}

export default function IntentSheet({ product, brandName, tripName, onClose }) {
  const open = !!product;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(true);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Canvas 2D render + cache — zero html2canvas
  useEffect(() => {
    if (!open) { setPreviewUrl(null); return; }
    let cancelled = false;

    const cached = _cardBlobCache.get(product?.id);
    if (cached) {
      setPreviewUrl(cached);
      setBusy(false);
      return;
    }

    setBusy(true);
    setPreviewUrl(null);

    const doRender = async () => {
      try {
        const b = await renderShareCardCanvas(product, { brandName, tripName });
        if (cancelled) return;
        if (!b) throw new Error("render null (tainted?)");
        const url = URL.createObjectURL(b);
        cacheCard(product.id, url);
        setPreviewUrl(url);
      } catch (e) {
        console.warn("card render failed", e);
        toast.error("Gagal membuat kartu. Coba lagi ya");
      } finally {
        if (!cancelled) setBusy(false);
      }
    };

    const timer = setTimeout(doRender, 50);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  const openWa = () => {
    if (!product?.wa_url) {
      toast.error("Kontak admin belum tersedia, coba lagi nanti");
      return;
    }
    api.post("/events", {
      product_id: product.id,
      type: "share",
      metadata: { share_method: "wa_open", card_source: previewUrl ? "client" : "none" },
    }).catch(() => {});
    window.open(product.wa_url, "_blank");
    setTimeout(() => onClose?.(), 300);
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[70] bg-plum/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        data-testid="intent-sheet-backdrop"
      />

      <div
        role="dialog"
        aria-label="Kirim kartu ke admin"
        className="fixed inset-x-0 bottom-0 z-[80] rounded-t-[32px] bg-cream dark:bg-plum text-plum dark:text-cream shadow-[0_-20px_60px_rgba(74,44,78,0.35)] animate-sheet-up max-h-[92dvh] overflow-y-auto"
        data-testid="intent-sheet"
      >
        <div className="flex justify-center pt-3">
          <span className="h-1.5 w-12 rounded-full bg-plum/25 dark:bg-cream/25" />
        </div>

        <div className="flex items-center justify-between px-6 pt-3 pb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-sakura-500" />
            <h2 className="font-serif italic text-2xl">Gas, order sekarang? 🔥</h2>
          </div>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full grid place-items-center bg-plum/10 dark:bg-cream/10 active:scale-95 transition"
            aria-label="Tutup"
            data-testid="intent-sheet-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mx-6 mt-2 mb-3 flex items-start gap-2 rounded-2xl bg-buttercream-100 dark:bg-cream/10 border border-buttercream-500/30 px-3.5 py-2.5">
          <Hand className="h-4 w-4 mt-0.5 shrink-0 text-buttercream-600" strokeWidth={2.25} />
          <p className="text-[11px] leading-relaxed text-plum/80 dark:text-cream/80">
            <b>Tekan lama gambar</b> untuk simpan ke galeri, lalu <b>Buka WhatsApp</b> & attach kartunya ya 💗
          </p>
        </div>

        <div className="px-6 pt-1 pb-4">
          <div
            className="rounded-3xl overflow-hidden border border-plum/10 dark:border-cream/10 bg-plum/5 dark:bg-cream/5 aspect-[4/5] relative"
            style={{ WebkitTouchCallout: "default", WebkitUserSelect: "auto", userSelect: "auto" }}
          >
            {busy && (
              <div className="absolute inset-0 grid place-items-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-6 w-6 text-sakura-500 animate-spin" />
                  <span className="text-xs uppercase tracking-[0.25em] text-plum/60 dark:text-cream/60">Bikin kartu…</span>
                </div>
              </div>
            )}
            {previewUrl && (
              <img
                src={previewUrl}
                alt={`Kartu ${product?.nama}`}
                draggable={true}
                crossOrigin="anonymous"
                className="h-full w-full object-cover pointer-events-auto"
                data-testid="intent-sheet-preview"
                style={{ WebkitTouchCallout: "default", WebkitUserSelect: "auto" }}
              />
            )}
          </div>

          <div className="mt-3 min-w-0">
            <div className="font-serif italic text-xl truncate">{product?.nama}</div>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="font-serif italic text-2xl font-semibold text-sakura-600 dark:text-sakura-300">{rp(product?.harga_idr || 0)}</span>
              {product?.harga_coret_idr > product?.harga_idr && (
                <>
                  <span className="text-xs text-sakura-400 line-through">{rp(product.harga_coret_idr)}</span>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[0.08em] bg-mint-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300 border border-emerald-500/20">
                    HEMAT {rp(product.harga_coret_idr - product.harga_idr)}
                  </span>
                </>
              )}
            </div>
            <div className="text-plum/60 dark:text-cream/60 text-[10px] mt-1.5 uppercase tracking-[0.2em]">
              Tekan lama gambar untuk simpan
            </div>
          </div>
        </div>

        <div className="px-6 pb-8 pb-[max(2rem,env(safe-area-inset-bottom))]">
          <button
            onClick={openWa}
            disabled={busy}
            data-testid="intent-share-wa"
            className="w-full h-14 rounded-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold text-[15px] shadow-[0_10px_30px_rgba(37,211,102,0.45)] active:scale-[0.98] transition disabled:opacity-60"
          >
            <MessageCircle className="h-5 w-5" />
            <span>Order via WhatsApp</span>
          </button>
          <p className="mt-2.5 text-center text-[10px] uppercase tracking-[0.2em] text-plum/50 dark:text-cream/50">
            Slot terbatas — admin fast respon ⚡
          </p>
          {busy && (
            <button
              onClick={openWa}
              data-testid="intent-skip-card"
              className="mt-3 w-full text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-plum/60 dark:text-cream/60 underline underline-offset-4 active:scale-[0.98] transition"
            >
              Lanjut tanpa kartu →
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
